import { useEffect, useRef, useState } from "react";
import { Heart, Star, Send, Sparkles, MessageCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { useLang, useAuth } from "@/context/AppContext";
import { useLiveEvent } from "@/context/RealtimeContext";
import { Input } from "@/components/ui/input";

export const SocialBar = ({ r, onChange, compact = false }) => {
  const { t } = useLang();
  const mine = r.my_sides.length > 0 && r.my_sides.every((s) => r.interest?.[s]);
  const otherSides = ["person", "company"].filter((s) => !r.my_sides.includes(s));
  const otherLikes = otherSides.some((s) => r.interest?.[s]);
  const toggle = async (e, path) => {
    e.preventDefault();
    e.stopPropagation();
    try { const { data } = await api.post(`/relationships/${r.id}/${path}`); onChange(data); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex items-center gap-2">
      <button onClick={(e) => toggle(e, "interest")} title={t("interest")} data-testid={`like-btn-${r.id}`}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-all duration-200 hover:scale-105 ${mine ? "border-rose-400/50 bg-rose-400/10 text-rose-300" : "border-white/10 text-slate-400 hover:text-rose-300"}`}>
        <Heart className={`h-4 w-4 ${mine ? "fill-current" : ""}`} />{!compact && t("interest")}
      </button>
      <button onClick={(e) => toggle(e, "favorite")} title={t("favorite")} data-testid={`favorite-btn-${r.id}`}
        className={`inline-flex items-center rounded-full border p-1.5 transition-all duration-200 hover:scale-105 ${r.favorite ? "border-yellow-400/50 bg-yellow-400/10 text-yellow-300" : "border-white/10 text-slate-400 hover:text-yellow-300"}`}>
        <Star className={`h-4 w-4 ${r.favorite ? "fill-current" : ""}`} />
      </button>
      {otherLikes && <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-rose-300" data-testid={`other-interest-${r.id}`}><Heart className="h-3 w-3 fill-current" />{!compact && t("interest_other")}</span>}
    </div>
  );
};

const Bubble = ({ mine, children, meta, testId }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={testId}>
    <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-emerald-500 text-slate-950" : "bg-white/5 text-slate-200"}`}>
      <p className="whitespace-pre-wrap">{children}</p>
      {meta && <p className={`mt-1 font-mono text-[10px] ${mine ? "text-slate-900/60" : "text-slate-500"}`}>{meta}</p>}
    </div>
  </div>
);

const Composer = ({ onSend, disabled, placeholder, testPrefix }) => {
  const { t } = useLang();
  const [text, setText] = useState("");
  const submit = (e) => { e.preventDefault(); if (!text.trim()) return; onSend(text.trim()); setText(""); };
  return (
    <form onSubmit={submit} className="flex gap-2 border-t border-white/5 pt-3">
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} disabled={disabled} data-testid={`${testPrefix}-input`} />
      <button disabled={disabled || !text.trim()} data-testid={`${testPrefix}-send-btn`} title={t("send")}
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-500 text-slate-950 transition-opacity hover:opacity-90 disabled:opacity-40"><Send className="h-4 w-4" /></button>
    </form>
  );
};

const useAutoScroll = (dep) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [dep]);
  return ref;
};

const PartyChat = ({ r }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const { data: msgs, setData: setMsgs, reload: load } = useApi(`/relationships/${r.id}/messages`, { initial: [] });
  const open = ["authorized", "active"].includes(r.state);
  useLiveEvent(["chat"], (m) => m.data.relationship_id === r.id && setMsgs((xs) => (xs.some((x) => x.id === m.data.id) ? xs : [...xs, m.data])));
  const ref = useAutoScroll(msgs.length);
  const send = async (text) => { try { await api.post(`/relationships/${r.id}/messages`, { text }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <div ref={ref} className="h-72 space-y-3 overflow-y-auto pr-1" data-testid="party-chat-messages">
        {!msgs.length && <p className="text-sm text-slate-500">{t("chat_empty")}</p>}
        {msgs.map((m) => <Bubble key={m.id} mine={m.sender_id === user.id} testId={`party-msg-${m.id}`} meta={`${m.sender_name} · ${t(m.side)} · ${new Date(m.created_at).toLocaleTimeString()}`}>{m.text}</Bubble>)}
      </div>
      {!open && <p className="mb-2 flex items-center gap-2 text-xs text-amber-300" data-testid="chat-locked"><Lock className="h-3.5 w-3.5" />{t("chat_locked")}</p>}
      <Composer onSend={send} disabled={!open} placeholder={t("chat_placeholder")} testPrefix="party-chat" />
    </>
  );
};

const handleEvent = (chunk, onDelta) => {
  const raw = chunk.replace(/^data: /, "");
  if (raw === "[DONE]") return;
  const ev = JSON.parse(raw);
  if (ev.error) toast.error(ev.error);
  if (ev.delta) onDelta(ev.delta);
};

const openStream = async (rid, text) => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/relationships/${rid}/assistant/stream`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
  });
  if (!res.ok) throw Object.assign(new Error(res.statusText), { response: { data: await res.json().catch(() => ({ detail: res.statusText })) } });
  return res.body.getReader();
};

const streamAssistant = async (rid, text, onDelta) => {
  const reader = await openStream(rid, text);
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => handleEvent(p, onDelta));
  }
};

const AssistantChat = ({ r }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const { data: msgs, setData: setMsgs } = useApi(`/relationships/${r.id}/assistant`, { initial: [] });
  const [busy, setBusy] = useState(false);
  const ref = useAutoScroll(msgs.map((m) => m.text).join("").length);
  const appendDelta = (delta) => setMsgs((m) => m.map((x) => (x.id === "streaming" ? { ...x, text: x.text + delta } : x)));

  const send = async (text) => {
    setBusy(true);
    setMsgs((m) => [...m, { id: `u${Date.now()}`, role: "user", text }, { id: "streaming", role: "assistant", text: "" }]);
    try {
      await streamAssistant(r.id, text, appendDelta);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMsgs((m) => m.map((x) => (x.id === "streaming" ? { ...x, id: `a${Date.now()}` } : x)));
      setBusy(false);
    }
  };

  if (!user.ai_consent) return <p className="h-72 text-sm text-slate-400" data-testid="assistant-needs-consent">{t("ai_needs_consent")}</p>;
  return (
    <>
      <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-yellow-300/80">{t("ai_chat_hint")}</p>
      <div ref={ref} className="h-64 space-y-3 overflow-y-auto pr-1" data-testid="assistant-chat-messages">
        {msgs.map((m) => <Bubble key={m.id} mine={m.role === "user"} testId={`assistant-msg-${m.role}`}>{m.text.replace(/\*\*/g, "") || "…"}</Bubble>)}
      </div>
      <Composer onSend={send} disabled={busy} placeholder={t("ai_chat_placeholder")} testPrefix="assistant-chat" />
    </>
  );
};

export const ChatPanel = ({ r }) => {
  const { t } = useLang();
  const [tab, setTab] = useState("party");
  const tabs = [["party", "chat_party", MessageCircle], ["ai", "chat_ai", Sparkles]];
  return (
    <div className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="chat-panel" id="chat">
      <div className="mb-4 flex items-center gap-2">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`chat-tab-${k}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs transition-colors duration-200 ${tab === k ? "bg-emerald-400/10 text-emerald-300" : "text-slate-400 hover:text-white"}`}>
            <Icon className="h-4 w-4" />{t(label)}
          </button>
        ))}
      </div>
      {tab === "party" ? <PartyChat r={r} /> : <AssistantChat r={r} />}
    </div>
  );
};
